<template>
  <div
    class="relative rounded-md overflow-hidden"
    :class="{ '!rounded-br-none': showDetails }"
  >
    <component
      :is="!href ? 'div' : TheNuxtLink"
      :href
      class="chapter-card__wrap chapter-card relative"
      :class="{
        small,
        'x-small': xSmall,
        context: !!$slots.contextMenu,
        transparent,
        read: isRead,
      }"
    >
      <div class="chapter-card__preview relative">
        <AsyncImage
          class="absolute inset-0 w-full h-full object-cover"
          :src="
            thumbnailUrl || getAbsoluteAssetLink('nami/banner/great-wave.png')
          "
        />
        <div
          v-if="isLocked && !isAccess"
          class="absolute inset-0 flex items-center justify-center bg-black/50"
        >
          <div class="p-1.5 bg-white/80 dark:bg-neutral-800/80 rounded-full">
            <IconLock class="block" />
          </div>
        </div>
      </div>
      <div class="chapter-card__title">
        <div
          v-if="!isRead"
          class="mb-0.5 w-2 h-2 rounded-full inline-block bg-nami-comi-blue"
          :class="{ 'w-1.5 h-1.5': small }"
        />
        <h3 class="truncate">
          {{ chapterName }}
        </h3>
        <span
          v-if="isLocked && !small && !xSmall"
          class="hidden sm:inline-flex items-center justify-center flex-shrink-0 text-sm gap-1 px-1.5 py-0.25 rounded-full"
          :class="{
            'border border-nami-comi-blue text-nami-comi-blue': !isAccess,
            'border border-yellow-600 dark:border-yellow-400 text-yellow-600 dark:text-yellow-400':
              isAccess,
            '!p-0.5 aspect-square': small || xSmall,
          }"
        >
          <IconCrown v-if="isAccess" size="1.25em" />
          <IconLock v-else size="1.25em" />
          <span
            v-if="!small && !xSmall"
            class="hidden sm:inline font-medium mr-0.5"
          >
            {{
              translate(
                `components.ChapterCardTemplate.label.${isAccess ? "subscribed" : "subscribers"}`,
              )
            }}
          </span>
        </span>
        <span
          v-if="isWip"
          class="font-medium text-xs uppercase bg-purple-400 text-white inline-block w-max px-1.5 rounded-full"
        >
          {{
            translate("components.ChapterCardTemplate.label.work_in_progress")
          }}
        </span>
      </div>
      <div
        class="chapter-card__options"
        v-if="!!$slots.contextMenu"
        @click.stop
      >
        <slot name="contextMenu"></slot>
      </div>
      <div class="chapter-card__info">
        <div
          v-if="isLocked"
          class="w-5 h-5 flex items-center justify-center flex-shrink-0 rounded-full my-auto aspect-square mr-auto"
          :class="{
            'sm:hidden': !small && !xSmall,
            'border border-nami-comi-blue text-nami-comi-blue': !isAccess,
            'border border-yellow-600 dark:border-yellow-400 text-yellow-600 dark:text-yellow-400':
              isAccess,
          }"
        >
          <IconCrown v-if="isAccess" size="1.1em" />
          <IconLock v-else size="1.1em" />
        </div>
        <button
          v-if="!!$slots.details"
          class="chapter-card__details-button"
          @click.prevent.stop="showDetails = !showDetails"
        >
          <IconChevronDown
            v-if="sm && !(small || xSmall)"
            size="1.2em"
            class="transform transition-all"
            :class="{ 'rotate-180': showDetails }"
          />
          <IconAlignLeft v-else size="1.2em" />
          <span v-show="!small && !xSmall" class="hidden sm:block">
            {{
              translate(
                `components.ChapterCardTemplate.button.${showDetails ? "hide_details" : "show_details"}`,
              )
            }}
          </span>
        </button>
        <div
          class="stats text-sm flex items-center responsive-gap whitespace-nowrap text-right"
        >
          <span v-if="mappedLocale" class="chapter-card__locale">
            {{ mappedLocale }}
          </span>
          <span v-if="views !== undefined" :title="views.toString()">
            <IconEye size="1.15em" />
            <span v-if="statsPending" class="loader" />
            <span v-else>{{ formatNumber(views) }}</span>
          </span>
          <span v-if="likes !== undefined" :title="likes.toString()">
            <IconHeart size="1.15em" />
            <span v-if="statsPending" class="loader" />
            <span v-else>{{ formatNumber(likes) }}</span>
          </span>
          <span v-if="comments !== undefined" :title="comments.toString()">
            <IconMessageDots size="1.15em" />
            <span v-if="statsPending" class="loader" />
            <span v-else>{{ formatNumber(comments) }}</span>
          </span>
          <span class="truncate" :title="publishDate?.toISOString()">
            <IconClock size="1.15em" />
            <span>{{ timestamp }}</span>
          </span>
        </div>
      </div>
    </component>
    <TransitionAutoHeight
      v-if="!!$slots.details && sm && !small && !xSmall"
      :model-value="showDetails"
    >
      <div class="chapter-card !h-auto">
        <div
          class="col-start-2 row-span-2 p-2 bg-neutral-100 dark:bg-neutral-800 rounded-b-md border-t border-neutral-300 dark:border-neutral-600"
        >
          <slot name="details"></slot>
        </div>
      </div>
    </TransitionAutoHeight>
    <NamiModal
      v-if="!!$slots.details && (!sm || small || xSmall)"
      :model-value="showDetails"
      @update:model-value="(v) => (showDetails = v)"
    >
      <NamiModalTitle @close="showDetails = false">
        {{ translate("components.ChapterCardTemplate.modal.details.title") }}
      </NamiModalTitle>
      <slot name="details"></slot>
    </NamiModal>
  </div>
</template>

<script setup lang="ts">
import {
  type ChapterKind,
  type VolumeKind,
  type AvailableLanguages,
} from "~/src/api";
import {
  IconEye,
  IconMessageDots,
  IconHeart,
  IconClock,
  IconLock,
  IconCrown,
  IconAlignLeft,
  IconChevronDown,
} from "@tabler/icons-vue";

export interface ChapterCardTemplateStyleProps {
  transparent?: boolean;
  small?: boolean;
  xSmall?: boolean;
  statsPending?: boolean;

  volumeKind?: VolumeKind | null;
  chapterKind?: ChapterKind | null;

  // states
  isWip?: boolean;
  isLocked?: boolean;
  isAccess?: boolean;
  isRead?: boolean;
}

interface ChapterCardTemplateProps extends ChapterCardTemplateStyleProps {
  name?: string;
  chapter?: string | null;
  volume?: string | null;
  thumbnailUrl?: string;

  // stats / meta
  language?: AvailableLanguages;
  likes?: number;
  comments?: number;
  views?: number;
  publishAt?: string | Date;

  href?: string;
}

const TheNuxtLink = resolveComponent("TheNuxtLink");

const {
  name,
  chapter,
  volume,
  volumeKind,
  chapterKind,
  language,
  publishAt,
  small,
  xSmall,
} = defineProps<ChapterCardTemplateProps>();

const mappedLocale = computed(() =>
  language ? reverseLocaleMap(language) : null,
);

const nuxtApp = useNuxtApp();
const { locale, t: translate } = nuxtApp.$i18n.global;
const { sm } = useBreakpoints();

const formatNumber = useNumberFormatter(locale);
const formatDate = useTimeAgo(locale);

const showDetails = ref(false);

const chapterName = computed(() => {
  const formattedName = createChapterName(
    chapter || null,
    volume || null,
    name || null,
    small || xSmall ? "short" : "long",
    chapterKind || undefined,
    volumeKind || undefined,
  );
  if (small || xSmall) return formattedName.replace(/ - .+/, "");
  return formattedName;
});

const publishDate = computed(() => {
  if (!publishAt) return null;
  if (publishAt instanceof Date) return publishAt;
  return new Date(publishAt);
});

const timestamp = computed(() => {
  if (!publishDate.value) return "N/A";
  return formatDate.value(publishDate.value, "mini");
});
</script>

<style scoped lang="postcss">
.chapter-card__wrap {
  &:not(.transparent) {
    @apply transition-colors duration-75
    bg-neutral-100 dark:bg-neutral-800
    canhover:hover:bg-neutral-200 canhover:dark:hover:bg-neutral-700
    active:bg-neutral-200 dark:active:bg-neutral-700;

    &:hover .chapter-card__locale {
      @apply bg-neutral-300 dark:bg-neutral-600;
    }
  }

  .chapter-card__options {
    @apply canhover:hidden;
  }

  &:hover .chapter-card__options,
  &:focus-within .chapter-card__options {
    display: initial;
  }
}

.chapter-card {
  height: 4rem;
  display: grid;
  grid-template-columns: 6rem 1fr min-content min-content min-content;
  grid-template-rows: min-content 1fr min-content;
  /*
  grid-template-areas:
    "preview title    title   title   spacer3"
    "preview spacer1  spacer1 spacer1 spacer3"
    "preview spacer2  spacer2 spacer2 spacer3"
    "preview org      info   info   spacer3"
   */
  grid-template-areas:
    "preview title    title   title "
    "preview spacer1  spacer1 spacer1"
    "preview info    info   info   ";

  &.context {
    /*
    grid-template-areas:
      "preview title    title   options spacer3"
      "preview spacer1  spacer1 spacer1 spacer3"
      "preview spacer2  spacer2 spacer2 spacer3"
      "preview org      info   info   spacer3"
     */
    grid-template-areas:
      "preview title    title   options"
      "preview spacer1  spacer1 spacer1"
      "preview info    info   info";
  }

  &:not(.small, .x-small) {
    @apply sm:h-[4.5rem] sm:grid-cols-[6.75rem_1fr_min-content_min-content_min-content] sm:text-base;
  }

  &.x-small {
    @apply h-12 grid-cols-[4.5rem_1fr_min-content_min-content_min-content];
  }

  @apply overflow-hidden text-sm;
}

.chapter-card__preview {
  grid-area: preview;
  @apply rounded-md overflow-hidden aspect-[3/2];
}

.chapter-card__title {
  grid-area: title;
  word-break: break-word;
  @apply flex items-center gap-x-2 font-bold ml-2 mt-2;
}

.chapter-card__options {
  grid-area: options;
  @apply sm:pt-1 pr-1;
}

.chapter-card__info {
  grid-area: info;
  @apply mx-2 mb-2 flex gap-x-1 justify-end;
}

.chapter-card__locale {
  @apply select-none rounded-md text-xs font-bold uppercase bg-neutral-200 dark:bg-neutral-700 px-1 py-0.5;
}

.chapter-card__details-button {
  @apply text-sm text-nami-comi-blue
  canhover:hover:bg-neutral-300 canhover:hover:dark:bg-neutral-600/70
  flex items-center gap-1
  p-2 -m-2 sm:p-1 sm:pr-2 sm:-m-1 mr-auto sm:mr-auto rounded-full;
}

.stats {
  & > span {
    @apply flex items-center space-x-1;
  }

  & .loader {
    @apply w-4 h-3 bg-neutral-400 animate-pulse rounded-sm;
  }
}
</style>
